import { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit2, Trash2, DollarSign, Tag, 
  Percent, FileText, Filter, MoreVertical, Check, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORIES = [
  'Room', 'Nursing', 'Admin', 'Lab', 'Pharmacy', 
  'Consultation', 'Procedure', 'Emergency', 'ICU', 'OT', 'Radiology', 'Other'
];

const CATEGORY_COLORS = {
  'Room': 'bg-blue-100 text-blue-700',
  'Nursing': 'bg-pink-100 text-pink-700',
  'Admin': 'bg-slate-100 text-slate-700',
  'Lab': 'bg-amber-100 text-amber-700',
  'Pharmacy': 'bg-emerald-100 text-emerald-700',
  'Consultation': 'bg-purple-100 text-purple-700',
  'Procedure': 'bg-red-100 text-red-700',
  'Emergency': 'bg-orange-100 text-orange-700',
  'ICU': 'bg-cyan-100 text-cyan-700',
  'OT': 'bg-indigo-100 text-indigo-700',
  'Radiology': 'bg-teal-100 text-teal-700',
  'Other': 'bg-gray-100 text-gray-700'
};

export default function FixedChargeMaster() {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [stats, setStats] = useState(null);
  
  const [formData, setFormData] = useState({
    charge_name: '',
    category: '',
    default_rate: '',
    tax_percentage: '0',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchCharges();
    fetchStats();
  }, [categoryFilter, statusFilter]);

  const fetchCharges = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (statusFilter !== 'all') params.append('is_active', statusFilter === 'active');
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await api.get(`/fixed-charges?${params.toString()}`);
      setCharges(response.data);
    } catch (error) {
      toast.error('Failed to load fixed charges');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/fixed-charges/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCharges();
  };

  const openModal = (charge = null) => {
    if (charge) {
      setEditingCharge(charge);
      setFormData({
        charge_name: charge.charge_name,
        category: charge.category,
        default_rate: charge.default_rate.toString(),
        tax_percentage: charge.tax_percentage.toString(),
        description: charge.description || '',
        is_active: charge.is_active
      });
    } else {
      setEditingCharge(null);
      setFormData({
        charge_name: '',
        category: '',
        default_rate: '',
        tax_percentage: '0',
        description: '',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.charge_name.trim()) {
      toast.error('Charge name is required');
      return;
    }
    if (!formData.category) {
      toast.error('Category is required');
      return;
    }
    if (!formData.default_rate || parseFloat(formData.default_rate) < 0) {
      toast.error('Valid default rate is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        default_rate: parseFloat(formData.default_rate),
        tax_percentage: parseFloat(formData.tax_percentage) || 0
      };

      if (editingCharge) {
        await api.put(`/fixed-charges/${editingCharge.id}`, payload);
        toast.success('Fixed charge updated successfully');
      } else {
        await api.post('/fixed-charges', payload);
        toast.success('Fixed charge created successfully');
      }

      setShowModal(false);
      fetchCharges();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save fixed charge');
    }
  };

  const handleDelete = async (charge) => {
    if (!window.confirm(`Are you sure you want to deactivate "${charge.charge_name}"?`)) {
      return;
    }

    try {
      await api.delete(`/fixed-charges/${charge.id}`);
      toast.success('Fixed charge deactivated');
      fetchCharges();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const filteredCharges = charges.filter(charge =>
    charge.charge_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="fixed-charge-master">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fixed Charge Master</h1>
          <p className="text-slate-600 text-sm mt-1">
            Manage standardized pricing for routine hospital charges
          </p>
        </div>
        <Button 
          onClick={() => openModal()} 
          className="bg-[#4A238F] hover:bg-[#3A1B72]"
          data-testid="add-charge-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Charge
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-slate-600">Total Charges</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Check className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active}</p>
                  <p className="text-xs text-slate-600">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <X className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.inactive}</p>
                  <p className="text-xs text-slate-600">Inactive</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Tag className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_category?.length || 0}</p>
                  <p className="text-xs text-slate-600">Categories</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search charges..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-charges"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-32" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="secondary">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Charges Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A238F]"></div>
            </div>
          ) : filteredCharges.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No fixed charges found</p>
              <Button onClick={() => openModal()} variant="link" className="mt-2">
                Add your first charge
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Charge Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Default Rate</TableHead>
                  <TableHead className="text-right">Tax %</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCharges.map((charge) => (
                  <TableRow key={charge.id} data-testid={`charge-row-${charge.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{charge.charge_name}</p>
                        {charge.description && (
                          <p className="text-xs text-slate-500 truncate max-w-[200px]">
                            {charge.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={CATEGORY_COLORS[charge.category] || 'bg-gray-100'}>
                        {charge.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(charge.default_rate)}
                    </TableCell>
                    <TableCell className="text-right">
                      {charge.tax_percentage}%
                    </TableCell>
                    <TableCell>
                      <Badge className={charge.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                        {charge.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openModal(charge)}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(charge)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingCharge ? 'Edit Fixed Charge' : 'Add New Fixed Charge'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="charge_name">Charge Name *</Label>
              <Input
                id="charge_name"
                placeholder="e.g., Nursing Care, Room Rent"
                value={formData.charge_name}
                onChange={(e) => setFormData({ ...formData, charge_name: e.target.value })}
                data-testid="charge-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="default_rate">Default Rate (₹) *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="default_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.default_rate}
                    onChange={(e) => setFormData({ ...formData, default_rate: e.target.value })}
                    className="pl-10"
                    data-testid="rate-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax_percentage">Tax Percentage</Label>
                <div className="relative">
                  <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="tax_percentage"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="0"
                    value={formData.tax_percentage}
                    onChange={(e) => setFormData({ ...formData, tax_percentage: e.target.value })}
                    className="pl-10"
                    data-testid="tax-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder="Additional details about this charge..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Active Status</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#4A238F] hover:bg-[#3A1B72]" data-testid="save-charge-btn">
                {editingCharge ? 'Update Charge' : 'Create Charge'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
